import React, { useCallback, useRef } from 'react';

import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Grid from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import Tooltip from '@mui/material/Tooltip';
import Toolbar from '@mui/material/Toolbar';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import ListItem from '@mui/material/ListItem';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemButton from '@mui/material/ListItemButton';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import { DefaultTheme } from '@mui/styles/defaultTheme';

// Inactive SVGs
import { ReactComponent as UcmLogoIcon } from 'src/images/UcmLogo.svg';
import { ReactComponent as FineIcon } from 'src/images/DrawerIcons/Fine.svg';
import { ReactComponent as HomeIcon } from 'src/images/DrawerIcons/Home.svg';
import { ReactComponent as GatekeeperIcon } from 'src/images/DrawerIcons/Door.svg';
import { ReactComponent as SetupIcon } from 'src/images/DrawerIcons/Setup.svg';
import { ReactComponent as LogoutIcon } from 'src/images/DrawerIcons/Logout.svg';
// import { ReactComponent as SearchIcon } from 'src/images/DrawerIcons/Search.svg';
import { ReactComponent as ProjectIcon } from 'src/images/DrawerIcons/Project.svg';
import { ReactComponent as StudentIcon } from 'src/images/DrawerIcons/Student.svg';
import { ReactComponent as ContractIcon } from 'src/images/DrawerIcons/Contract.svg';
// import { ReactComponent as ConditionIcon } from 'src/images/DrawerIcons/Condition.svg';
import { ReactComponent as TimeTrackingIcon } from 'src/images/DrawerIcons/TimeTracking.svg';
import { ReactComponent as CancellationIcon } from 'src/images/DrawerIcons/Cancellation.svg';
import { ReactComponent as VerificationIcon } from 'src/images/DrawerIcons/Verification.svg';
import { ReactComponent as PayrollIcon } from 'src/images/DrawerIcons/Payroll.svg';
// import { ReactComponent as AutoAcceptanceIcon } from 'src/images/DrawerIcons/AutoAcceptance.svg';

// Active SVGs
import { ReactComponent as ActiveFineIcon } from 'src/images/DrawerIcons/ActiveFine.svg';
import { ReactComponent as ActiveHomeIcon } from 'src/images/DrawerIcons/ActiveHome.svg';
import { ReactComponent as ActiveSetupIcon } from 'src/images/DrawerIcons/ActiveSetup.svg';
// import { ReactComponent as ActiveSearchIcon } from 'src/images/DrawerIcons/ActiveSearch.svg';
import { ReactComponent as ActiveProjectIcon } from 'src/images/DrawerIcons/ActiveProject.svg';
import { ReactComponent as ActiveStudentIcon } from 'src/images/DrawerIcons/ActiveStudent.svg';
import { ReactComponent as ActiveGatekeeperIcon } from 'src/images/DrawerIcons/ActiveDoor.svg';
import { ReactComponent as ActiveContractIcon } from 'src/images/DrawerIcons/ActiveContract.svg';
// import { ReactComponent as ActiveConditionIcon } from 'src/images/DrawerIcons/ActiveCondition.svg';
import { ReactComponent as ActiveTimeTrackingIcon } from 'src/images/DrawerIcons/ActiveTimeTracking.svg';
import { ReactComponent as ActiveCancellationIcon } from 'src/images/DrawerIcons/ActiveCancellation.svg';
import { ReactComponent as ActiveVerificationIcon } from 'src/images/DrawerIcons/ActiveVerification.svg';
import { ReactComponent as ActivePayrollIcon } from 'src/images/DrawerIcons/ActivePayroll.svg';
// import { ReactComponent as ActiveAutoAcceptanceIcon } from 'src/images/DrawerIcons/ActiveAutoAcceptance.svg';

import useMessenger from 'src/utils/hooks/useMessenger';
import { removeAccessToken } from 'src/utils/authentication';
import NavTabs, { type NavTabProps } from 'src/components/NavTabs';

import messages from './messages';

export const UnderConstructionMessage = ({ text }: { text?: string }) => (
  <div>
    Oops! The "{text?.toUpperCase()}" feature is still under construction.
    <br />
    We're working hard to bring it to you soon. Thanks for your patience!
  </div>
);

const DRAWER_WIDTH = 70;

const useStyles = makeStyles((theme) => ({
  appBar: {
    paddingLeft: DRAWER_WIDTH,
  },
  drawer: {
    '& .MuiDrawer-paper': {
      alignItems: 'center',
    },
  },
  listItemIcon: {
    justifyContent: 'center',
  },
  content: {
    marginLeft: DRAWER_WIDTH,
    padding: theme.spacing(3),
    paddingTop: theme.spacing(12),
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: 'inherit',
  },
}));

interface MenuItemType {
  url: string;
  value: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  activeIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

export const menuItems: Array<MenuItemType> = [
  {
    label: 'Home',
    value: 'home',
    icon: HomeIcon,
    activeIcon: ActiveHomeIcon,
    url: '/home',
  },
  // {
  //   label: 'Search',
  //   value: 'search',
  //   icon: SearchIcon,
  //   activeIcon: ActiveSearchIcon,
  //   url: 'disabled',
  // },
  {
    label: 'Projects',
    value: 'projects',
    icon: ProjectIcon,
    activeIcon: ActiveProjectIcon,
    url: '/projects',
  },
  {
    label: 'Cancellations',
    value: 'cancellations',
    icon: CancellationIcon,
    activeIcon: ActiveCancellationIcon,
    url: '/cancellations',
  },
  {
    label: 'Students',
    value: 'students',
    icon: StudentIcon,
    activeIcon: ActiveStudentIcon,
    url: '/students',
  },
  {
    label: 'Verification',
    value: 'verification',
    icon: VerificationIcon,
    activeIcon: ActiveVerificationIcon,
    url: '/verifications',
  },
  {
    label: 'Contracts',
    value: 'contracts',
    icon: ContractIcon,
    activeIcon: ActiveContractIcon,
    url: '/contracts',
  },
  {
    label: 'Time Tracking',
    value: 'time_tracking',
    icon: TimeTrackingIcon,
    activeIcon: ActiveTimeTrackingIcon,
    url: '/time_tracking',
  },
  {
    label: 'Fines',
    value: 'fines',
    icon: FineIcon,
    activeIcon: ActiveFineIcon,
    url: '/fines',
  },
  // {
  //   label: 'Conditions',
  //   value: 'conditions',
  //   icon: ConditionIcon,
  //   activeIcon: ActiveConditionIcon,
  //   url: 'disabled',
  // },
  {
    label: 'Gatekeeper',
    value: 'gatekeeper',
    icon: GatekeeperIcon,
    activeIcon: ActiveGatekeeperIcon,
    url: '/gatekeeper',
  },
  {
    label: 'Payroll',
    value: 'payroll',
    icon: PayrollIcon,
    activeIcon: ActivePayrollIcon,
    url: '/payroll',
  },
  // {
  //   label: 'Auto Acceptance',
  //   value: 'auto_acceptance',
  //   icon: AutoAcceptanceIcon,
  //   activeIcon: ActiveAutoAcceptanceIcon,
  //   url: '/auto_acceptance',
  // },
  {
    label: 'Setup',
    value: 'setup',
    icon: SetupIcon,
    activeIcon: ActiveSetupIcon,
    url: '/setup',
  },
];

type PageIconType =
  | 'home'
  | 'search'
  | 'projects'
  | 'cancellations'
  | 'students'
  | 'verification'
  | 'contracts'
  | 'time_tracking'
  | 'fines'
  | 'conditions'
  | 'gatekeeper'
  | 'setup'
  | 'payroll'
  | 'auto_acceptance';
interface GenericPageProps<T> extends NavTabProps<T> {
  title?: string;
  backHref?: string;
  iconName?: PageIconType;
  children: React.ReactNode;
}

export default function PageLayout<T extends string>({
  title,
  iconName,
  backHref,
  children,
  ...navProps
}: GenericPageProps<T>) {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const messenger = useMessenger();
  const { formatMessage } = useIntl();

  const handleMenuItemClick = useCallback((url: string, text?: string) => {
    url === 'disabled'
      ? messenger.info(<UnderConstructionMessage text={text} />)
      : navigate(url);
  }, []);

  const getTitleIcon = useCallback(() => {
    const currentItem = menuItems.find(({ value }) => value === iconName);
    return currentItem ? currentItem.activeIcon : null;
  }, [iconName]);

  const handleLogout = useCallback(() => {
    removeAccessToken();
    handleMenuItemClick('/login');
  }, []);

  const ActiveIcon = getTitleIcon();

  const arrowStylesSx = useRef((theme: DefaultTheme) => ({
    color: theme.palette.grey[400],
    mr: 0.5,
  })).current;

  const renderIcon = useCallback(
    (
      url: string,
      Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>,
      ActiveIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>,
    ) => {
      const isActive = (() => {
        switch (url) {
          case '/setup':
            return (
              location.pathname.includes('/setup') ||
              location.pathname.includes('/categories') ||
              location.pathname.includes('/tutorial_details')
            );
          case '/projects':
            return (
              location.pathname.includes('/projects') ||
              location.pathname.includes('/project_details')
            );
          default:
            return location.pathname.includes(url);
        }
      })();

      const iconSize = isActive ? 32 : 24;
      const IconComponent = isActive ? ActiveIcon : Icon;
      return <IconComponent width={iconSize} height={iconSize} />;
    },
    [location.pathname],
  );

  return (
    <>
      <MuiAppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <Grid container alignItems="center" spacing={2}>
            {backHref && (
              <Link to={backHref} className={classes.backLink}>
                <ArrowCircleLeftIcon sx={arrowStylesSx} fontSize="large" />
                <Typography variant="body1" color="textPrimary">
                  {formatMessage(messages.back)}
                </Typography>
              </Link>
            )}

            <Grid>
              {ActiveIcon != null && <ActiveIcon width={32} height={32} />}
            </Grid>

            {title && (
              <Grid>
                <Typography variant="h4" noWrap>
                  {title}
                </Typography>
              </Grid>
            )}

            <Grid>
              <NavTabs {...navProps} />
            </Grid>
          </Grid>
        </Toolbar>
      </MuiAppBar>

      <MuiDrawer variant="permanent" className={classes.drawer}>
        <Box pt={1.5} pb={1}>
          <UcmLogoIcon height={40} width={DRAWER_WIDTH} />
        </Box>

        <Stack flex={1} justifyContent="space-between">
          <List>
            {menuItems.map(
              ({ value, label, icon: Icon, activeIcon: ActiveIcon, url }) => (
                <ListItem key={value} disablePadding>
                  <Tooltip title={label} placement="right">
                    <ListItemButton
                      sx={{ px: 0 }}
                      onClick={() => handleMenuItemClick(url, label)}
                    >
                      <ListItemIcon className={classes.listItemIcon}>
                        {renderIcon(url, Icon, ActiveIcon)}
                      </ListItemIcon>
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ),
            )}
          </List>

          <List>
            <ListItem disablePadding>
              <Tooltip title="Logout" placement="right">
                <ListItemButton sx={{ px: 0 }} onClick={handleLogout}>
                  <ListItemIcon className={classes.listItemIcon}>
                    <LogoutIcon />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </List>
        </Stack>
      </MuiDrawer>

      <Box className={classes.content}>{children}</Box>
    </>
  );
}
